import React from 'react';
import { PIN_PATHS, PIN_VIEWBOX } from './placePin.js';
import { findAddressAt } from './placePick.js';
import './placeDrag.css';

// Kartvy's Adress button on desktop as a pin you take out and drop (the owner, 2026-11-01, from the "Dra ut nålen"
// canvas: B and C combined): pointing at the button, its pin leans out towards the map with "Dra ut på kartan"
// beside it; pulling it out it pops free (rings burst from the button, which is left an empty dashed slot), and
// it follows the pointer, floating above a shadow that marks where its tip lands, with the address under the tip
// once the pointer rests. Let go on the map: it drops there with its place card (onDrop with the address), or,
// with no address with a number close by, glides back home with the "click an address" hint. Let go anywhere
// else, or Escape: it glides back. A plain click still turns the click-to-pick mode on and off.

const DRAG_START_PX = 6;
// How far above its tip the pin floats while carried.
const LIFT_PX = 16;
// The address under the tip is asked for once the pointer has rested, at most once a second (Nominatim's rule).
const LOOKUP_REST_MS = 380;
const LOOKUP_GAP_MS = 1000;
const RETURN_MS = 280;
// The carried pin's size, and its tip in the drawing (PIN_VIEWBOX: "-3 -3 745 689", the tip at 372.6, 607.5).
const PIN_W = 44;
const PIN_H = Math.round((PIN_W * 689) / 745);
const TIP_X = (PIN_W * (372.6 + 3)) / 745;
const TIP_Y = (PIN_H * (607.5 + 3)) / 689;
// Where a drop doesn't count as the map: the map's own controls and overlays.
const NOT_MAP = '.leaflet-control, .leaflet-geosearch-bar, .map-legend-drawer, .navBottomContainer, .kartvy-list-rail, .compare-card, .place-card, .map-topright-btn';

// The button's icon: the site's pin in white with the mark in green, in the menu's green circle.
export function PlaceDragIcon({ swedish }) {
    return (
        <span className="place-drag-icon">
            <svg viewBox="120 60 505 570" aria-hidden="true">
                <path className="place-drag-icon-drop" d="M372.6,80.2c126.6,0,229.1,102.6,229.1,229.1s-229.1,295-229.1,298.2S143.5,435.9,143.5,309.3S246.1,80.2,372.6,80.2z" />
                <path className="place-drag-icon-mark" d="M203.9,345c-3-12.6-4.4-25.4-4.4-38.4c0-95,80.9-167,167-167c67.8,0,130.9,44.6,157.9,112.6" />
                <path className="place-drag-icon-mark" d="M531.2,333.9c-15.1,91-101.1,152.6-192.2,137.4c-66.5-11-119.8-60.8-135.3-126.4" />
                <path className="place-drag-icon-mark" d="M532.8,344.3l-151.1-93.5c-8.4-5.2-19-5.2-27.3,0L201,344.5" />
            </svg>
            <span className="place-drag-tip" aria-hidden="true">{swedish ? 'Dra ut på kartan' : 'Drag onto the map'}</span>
        </span>
    );
}

const texts = (swedish) => ({
    drop: swedish ? 'Släpp för prishistoriken' : 'Drop for the price history',
    none: swedish ? 'Ingen adress här' : 'No address here',
    looking: swedish ? 'Letar…' : 'Looking…',
});

// map: the Leaflet map; button: the Adress button. getSwedish(): the language now. onLift() when the pin pops
// out (the map's pin goes then: there's only ever one). onDrop(found, latlng) with findAddressAt's address;
// onNoAddress() and onError() when a drop finds none or the lookup fails.
export const createPlaceDrag = ({ map, button, getSwedish, onLift, onDrop, onNoAddress, onError }) => {
    const circle = () => button.querySelector('.place-drag-icon');
    let start = null; // { x, y, id }
    let dragging = false;
    let suppressClickUntil = 0;
    let layer = null;
    let pin = null;
    let shadow = null;
    let label = null;
    let overMap = false;
    let point = null; // the tip's container point
    let restTimer = null;
    let lastLookupAt = 0;
    let lookupSeq = 0;
    let lastFound = { key: '', result: undefined }; // the last lookup's point and answer

    const setLabel = (title, sub) => {
        if (!label) return;
        label.firstChild.textContent = title;
        label.lastChild.textContent = sub;
        label.classList.toggle('is-empty', !title);
    };

    const build = () => {
        layer = document.createElement('div');
        layer.className = 'place-drag-layer';
        shadow = document.createElement('span');
        shadow.className = 'place-drag-shadow';
        pin = document.createElement('span');
        pin.className = 'place-drag-pin';
        pin.innerHTML = `<svg width="${PIN_W}" height="${PIN_H}" viewBox="${PIN_VIEWBOX}" aria-hidden="true">${PIN_PATHS}</svg>`;
        label = document.createElement('span');
        label.className = 'place-drag-label is-empty';
        label.innerHTML = '<b></b><span></span>';
        layer.append(shadow, label, pin);
        document.body.appendChild(layer);
    };

    const placeAt = (x, y) => {
        pin.style.transform = `translate(${x - TIP_X}px, ${y - LIFT_PX - TIP_Y}px) rotate(-8deg)`;
        shadow.style.transform = `translate(${x}px, ${y}px)`;
        label.style.transform = `translate(${x}px, ${y + 14}px)`;
    };

    const pointKey = (p) => `${Math.round(p.x / 4)}:${Math.round(p.y / 4)}`;

    const lookup = () => {
        if (!point || !overMap) return;
        const wait = LOOKUP_GAP_MS - (Date.now() - lastLookupAt);
        if (wait > 0) {
            restTimer = setTimeout(lookup, wait);
            return;
        }
        lastLookupAt = Date.now();
        const asked = point;
        const key = pointKey(asked);
        const mine = ++lookupSeq;
        const { lat, lng } = map.containerPointToLatLng(asked);
        setLabel(texts(getSwedish()).looking, '');
        findAddressAt(lat, lng)
            .then((found) => {
                lastFound = { key, result: found };
                if (mine !== lookupSeq || !dragging) return;
                const t = texts(getSwedish());
                if (found) setLabel(found.title, t.drop);
                else setLabel(t.none, '');
            })
            .catch(() => {
                if (mine === lookupSeq && dragging) setLabel('', '');
            });
    };

    const begin = (x, y) => {
        dragging = true;
        build();
        placeAt(x, y);
        // The pin pops out of its button: rings burst from the circle, which is left an empty dashed slot.
        button.classList.add('is-dragging-out');
        circle()?.classList.remove('is-popped');
        void circle()?.offsetWidth; // restart the burst
        circle()?.classList.add('is-popped');
        document.body.classList.add('is-place-dragging');
        onLift?.();
    };

    const track = (x, y) => {
        placeAt(x, y);
        const under = document.elementFromPoint(x, y);
        const container = map.getContainer();
        overMap = Boolean(under && container.contains(under) && !under.closest(NOT_MAP));
        layer.classList.toggle('is-over-map', overMap);
        const rect = container.getBoundingClientRect();
        point = overMap ? { x: x - rect.left, y: y - rect.top } : null;
        clearTimeout(restTimer);
        lookupSeq += 1; // an answer for where the pointer was is no longer wanted
        setLabel('', '');
        if (overMap) restTimer = setTimeout(lookup, LOOKUP_REST_MS);
    };

    const removeLayer = () => {
        layer?.remove();
        layer = null;
        pin = null;
        shadow = null;
        label = null;
    };

    const finish = () => {
        dragging = false;
        clearTimeout(restTimer);
        lookupSeq += 1;
        document.body.classList.remove('is-place-dragging');
        button.classList.remove('is-dragging-out');
        circle()?.classList.remove('is-popped');
        suppressClickUntil = Date.now() + 400;
    };

    // Back into the button, then the button gets its pin again.
    const glideHome = () => {
        const target = circle()?.getBoundingClientRect();
        if (!pin || !target) {
            removeLayer();
            finish();
            return;
        }
        layer.classList.add('is-returning');
        layer.classList.remove('is-over-map');
        pin.style.transition = `transform ${RETURN_MS}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${RETURN_MS}ms ease`;
        pin.style.transform = `translate(${target.left + (target.width / 2) - (PIN_W / 2)}px, ${target.top + (target.height / 2) - (PIN_H / 2)}px) scale(0.55)`;
        pin.style.opacity = '0.4';
        setTimeout(() => {
            removeLayer();
            finish();
            button.classList.add('is-returned');
            setTimeout(() => button.classList.remove('is-returned'), 360);
        }, RETURN_MS);
    };

    const drop = () => {
        const dropped = point;
        const { lat, lng } = map.containerPointToLatLng(dropped);
        const key = pointKey(dropped);
        // Down onto its tip while the address is had.
        layer.classList.add('is-dropping');
        pin.style.transition = 'transform 140ms cubic-bezier(0.5, 0, 0.75, 0)';
        const rect = map.getContainer().getBoundingClientRect();
        pin.style.transform = `translate(${rect.left + dropped.x - TIP_X}px, ${rect.top + dropped.y - TIP_Y}px)`;
        const answer = lastFound.key === key && lastFound.result !== undefined
            ? Promise.resolve(lastFound.result)
            : findAddressAt(lat, lng);
        answer
            .then((found) => {
                if (found) {
                    removeLayer();
                    finish();
                    onDrop(found, { lat, lng });
                    return;
                }
                onNoAddress();
                glideHome();
            })
            .catch(() => {
                onError();
                glideHome();
            });
    };

    const onMove = (event) => {
        if (!start || event.pointerId !== start.id) return;
        if (!dragging) {
            if (Math.hypot(event.clientX - start.x, event.clientY - start.y) < DRAG_START_PX) return;
            begin(event.clientX, event.clientY);
        }
        event.preventDefault();
        track(event.clientX, event.clientY);
    };
    const stopListening = () => {
        window.removeEventListener('pointermove', onMove, true);
        window.removeEventListener('pointerup', onUp, true);
        window.removeEventListener('pointercancel', onCancel, true);
        window.removeEventListener('keydown', onKey, true);
        start = null;
    };
    const onUp = (event) => {
        if (!start || event.pointerId !== start.id) return;
        stopListening();
        if (!dragging) return;
        if (overMap && point) drop();
        else glideHome();
    };
    const onCancel = () => {
        stopListening();
        if (dragging) glideHome();
    };
    const onKey = (event) => {
        if (event.key !== 'Escape' || !dragging) return;
        event.stopPropagation();
        stopListening();
        glideHome();
    };
    const onDown = (event) => {
        if (!event.isPrimary || event.button !== 0 || dragging) return;
        start = { x: event.clientX, y: event.clientY, id: event.pointerId };
        window.addEventListener('pointermove', onMove, true);
        window.addEventListener('pointerup', onUp, true);
        window.addEventListener('pointercancel', onCancel, true);
        window.addEventListener('keydown', onKey, true);
    };
    // A drag ends with a click on whatever it ended over: not the button's own (which turns the mode on or off).
    const onClickCapture = (event) => {
        if (Date.now() < suppressClickUntil) {
            event.stopPropagation();
            event.preventDefault();
        }
    };
    const onDragStart = (event) => event.preventDefault();

    button.addEventListener('pointerdown', onDown);
    button.addEventListener('dragstart', onDragStart);
    window.addEventListener('click', onClickCapture, true);

    return {
        destroy: () => {
            stopListening();
            clearTimeout(restTimer);
            removeLayer();
            if (dragging) finish();
            button.removeEventListener('pointerdown', onDown);
            button.removeEventListener('dragstart', onDragStart);
            window.removeEventListener('click', onClickCapture, true);
        },
    };
};

// The dropped pin lands with a small bounce (the "Gå till" pin's element, placePin.js).
export const bounceDroppedPin = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
        const element = document.querySelector('.place-pin');
        if (!element) return;
        element.classList.remove('is-dropped');
        void element.offsetWidth;
        element.classList.add('is-dropped');
        setTimeout(() => element.classList.remove('is-dropped'), 700);
    }));
};
